const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/LoginPage/FormContent/FormContent.tsx';
import * as React from 'react';
import { useRef, useState } from 'react';
import * as Styles from './styles';
import { InputField } from '../InputField/InputField';
import { ErrorMsg } from 'App/components/ErrorMsg/ErrorMsg';
import { Icon, Link, TextFieldProps } from '@mui/material';
import textMap from '../translations';
import { useIntl } from 'react-intl';
import { Gap, GapBig, GapSm } from 'App/components/Gap/Gap';
import { useDispatch } from 'react-redux';
import { resetLoginState, sendSmsFromInvitation, validateSmsCode } from 'App/actions/fixme.login';
import { QuickLoginBtn } from 'App/components/Buttons/QuickLoginBtn';
import { hasNativeWrapper } from 'App/utils/generalUtils';
import { dispatchPatappQRCodePending } from 'App/actions/nativeAppQRCodeScanner';
import MuiIconBtn from '@mui/material/IconButton';
import { css, cx } from '@emotion/css';
import { LoginStage } from 'App/pages';
import useBreakpoint from '@cistec/common_web/hooks/useBreakpoint';
import { Theme, useTheme } from '@mui/material/styles';
import { StartPageModal } from '../../StartPage/StartPageModal';
import LoadingSpinner from '@cistec/common_web/components/LoadingSpinner';
import { LoginData } from 'App/pages/LoginPage/LoginSteps';
import { Alert } from 'App/components/Alert/Alert';
import { CircularCountdown } from 'App/pages/LoginPage/SmsCodeInfo/CircularCountdown';
import moment from 'moment/moment';
import Button from '@mui/material/Button';
import useCountdown from 'App/utils/hooks/countdown';
import { MaskedTextField } from '@cistec/common_web/components/MaskedTextField/MaskedTextField';

/**
 * Form data for all stages
 */
export interface FormData {
    invitationCode: string;
    smsCode: string;
}

const sxTextField: TextFieldProps['sx'] = {
    mt: 0.5,
    mb: 0.5,
};

/**
 * Error state
 */
export type FormError = 'invitation-code-missing' | 'invitation-code-bad' | 'sms-code-missing' | 'sms-code-bad';

const getClasses = (theme: Theme) => ({
    cancelButton: css`
        flex: 0 0 auto;
    `,
    loginButton: css`
        margin-top: ${theme.spacing(3)};
        margin-bottom: ${theme.spacing(1)};
        display: flex;
        gap: 8px;
    `,
    btnRoot: css`
        text-transform: inherit;
    `,
    errorBox: css`
        align-items: center;

        & p {
            margin: 0;
        }
    `,
    twoFactorWrapper: css`
        display: flex;
        align-items: center;
    `,
    circularCountdown: css`
        opacity: 0.5;
        @media (max-width: 480px) {
            margin-left: 8px;
        }
    `,
    invitaionCode: css`
        & > div {
            background: white;
            width: 145px;

            & > input {
                padding: 8px;
                text-align: center;
            }
        }
    `,
});

interface Props {
    stage: LoginStage;
    prefillInvitationCode?: string | undefined;
    isActive: boolean;
    isLoading?: boolean;
    formError?: FormError;
    loginError?: string;
    setStage: (stage: LoginStage) => void;
    loginData: LoginData;
}

/*
 * TODO: This component is has to much of clutter and is not very transparent, when to show what. We need to refactor this.
 *  1. No Forms
 *  2. Each Stage should be an own Component.
 *  3. Remove the need of FormContent so the parent component of this component handles the rest.
 */
export const FormContent = (props: Props) => {
    const theme = useTheme();
    const classes = getClasses(theme);
    const isSmallDevice = useBreakpoint(theme?.breakpoints?.values?.sm);

    const [isModalVisible, setIsModalVisible] = useState(false);

    const [valid, setValid] = useState({ isCodeBad: false, isCodeMissing: false });
    const dispatch = useDispatch<any>();
    const { formatMessage } = useIntl();
    const formRef = useRef(undefined);
    const isStageInvCode = props.stage === 'invitation-code';
    const isStageSMSCode = props.stage === 'sms-code';
    const hasLoginBtn = !props.isLoading;
    const hasCancelBtn = isStageSMSCode && !props.isLoading;
    const hasQuickLoginBtn = isStageInvCode && process.env.NODE_ENV !== 'production' && !props.isLoading;
    const [verificationCode, setVerificationCode] = useState('');
    const externalError = props.loginError && <ErrorMsg error={props.loginError} />;

    const { timeLeft } = useCountdown(moment(props.loginData.loginCodeCreationDate), props.loginData.codeLifeTime);

    // disable the button either when countdown hits zero or no attempts are left.
    const isNotAllowedToSendSmsCode = isStageSMSCode && (props.loginData.loginAttemptsLeft === 0 || timeLeft === 0);

    const getButtonText = () => {
        const labelMap = {
            'invitation-code': textMap.loginBtnLabelCode,
            'sms-code': textMap.loginBtnLabelSMS,
        };

        return props.stage ? formatMessage(labelMap[props.stage]) : undefined;
    };

    const getFormErrorMessage = () => {
        if (valid.isCodeMissing) {
            const missingMap = {
                'sms-code': textMap.smsCodeEmpty,
                'invitation-code': textMap.invitationCodeEmpty,
            };

            return props.stage ? formatMessage(missingMap[props.stage]) : undefined;
        }

        if (valid.isCodeBad) {
            return formatMessage(textMap.invitationCodeBad);
        }

        return undefined;
    };

    const onVerificationCodeSubmitBtnClicked = (code: string) => {
        if (!mayBeValidInvitationCode(code)) {
            setValid({ isCodeMissing: false, isCodeBad: true });
        } else {
            setValid({ isCodeMissing: false, isCodeBad: false });
            setVerificationCode(code);
            dispatch(sendSmsFromInvitation(code) as any); // action uses thunk
        }
    };
    const onSmsCodeSubmitBtnClicked = (code: string) => {
        if (!mayBeValidSmsCode(code)) {
            setValid({ isCodeMissing: false, isCodeBad: true });
        } else {
            setValid({ isCodeMissing: false, isCodeBad: false });
            if (!verificationCode) {
                // go back to login
                onCancel();
            } else if (!isNotAllowedToSendSmsCode) {
                dispatch(validateSmsCode(code, verificationCode));
            }
        }
    };

    const handleQRCode = () => {
        dispatch(
            dispatchPatappQRCodePending(
                '((\\?invitationcode=)(.*))',
                formatMessage(textMap.QRCodeWithInvitationCode),
                formatMessage(textMap.QRCodeBackBtn)
            )
        );
        //  dispatchActions({ type: NATIVE_PATAPP_QRCODE_PENDING, payload: { regex: '((\\?invitationcode=)(.*))' } });
    };
    const onSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
        evt.stopPropagation();
        evt.preventDefault();
        const data: FormData = {
            invitationCode: getElementValue(formRef.current, 'invitation-code').replace(/-/g, ''),
            smsCode: getElementValue(formRef.current, 'sms-code'),
        };
        if (data.invitationCode?.length === 0 && data.smsCode?.length === 0) {
            setValid({ isCodeMissing: true, isCodeBad: false });
        } else {
            data.invitationCode && isStageInvCode && onVerificationCodeSubmitBtnClicked(data.invitationCode);
            data.smsCode && isStageSMSCode && onSmsCodeSubmitBtnClicked(data.smsCode);
        }
    };
    const onCancel = () => {
        if (isStageSMSCode) {
            setIsModalVisible(true);
        } else {
            setValid({ isCodeMissing: false, isCodeBad: false });
            dispatch(resetLoginState());
        }
    };

    const closeModal = () => {
        setIsModalVisible(false);
    };

    const goBackToInvitationCode = () => {
        dispatch(resetLoginState());
        props.setStage('invitation-code');
        closeModal();
    };

    const getAdditionalErrorForSmsCode = () => {
        if (props.loginData.loginAttemptsLeft) {
            return formatMessage(textMap.smsCodeAttemptsLeft, { count: props.loginData.loginAttemptsLeft });
        } else {
            return formatMessage(textMap.smsCodeNoAttemptsLeft);
        }
    };

    return (
        <form
            className={isSmallDevice ? Styles.squashedSmallDevice : undefined}
            /* type imcompatibility */
            ref={formRef as any}
            onSubmit={onSubmit}
        >
            <GapSm />
            {(!!getFormErrorMessage() || !!externalError || timeLeft === 0) && (
                <>
                    <Gap />
                    {timeLeft === 0 ? (
                        <Alert color={'danger'} className={classes.errorBox}>
                            <div>
                                {formatMessage(textMap.smsCodeExpired)}
                                <br />
                                {formatMessage(textMap.restartLoginProcess, {
                                    link: (
                                        <Link variant={'body2'} onClick={goBackToInvitationCode}>
                                            {formatMessage(textMap.clickHere)}
                                        </Link>
                                    ),
                                })}
                            </div>
                        </Alert>
                    ) : (
                        <Alert color={'danger'} className={classes.errorBox}>
                            <>
                                <div>{getFormErrorMessage() ?? externalError}</div>
                                {isStageSMSCode && getAdditionalErrorForSmsCode()}
                            </>
                        </Alert>
                    )}
                    <Gap />
                </>
            )}
            <GapSm />
            {isStageInvCode && (
                <>
                    <div
                        className={cx(
                            isSmallDevice ? Styles.squashedSmallDeviceGrid : undefined,
                            hasNativeWrapper && isSmallDevice ? Styles.addLeftMargin : undefined
                        )}
                    >
                        <MaskedTextField
                            autoFocus
                            value={props.prefillInvitationCode}
                            id={'login.label'}
                            readOnly={!props.isActive}
                            mask={'****-****-****'}
                            className={classes.invitaionCode}
                            name={props.stage}
                            sx={sxTextField}
                        />
                        {hasNativeWrapper && (
                            <MuiIconBtn
                                color="primary"
                                onClick={handleQRCode}
                                aria-label={formatMessage(textMap.invitationCodeBad)}
                            >
                                <Icon className="icons8-qr-code" />
                            </MuiIconBtn>
                        )}
                    </div>

                    <Gap />
                </>
            )}
            {isStageSMSCode && (
                <div className={classes.twoFactorWrapper}>
                    <InputField
                        id={'login.label'}
                        type="number"
                        stage={props.stage}
                        externalError={externalError}
                        disabled={!props.isActive}
                    />
                    <CircularCountdown
                        className={classes.circularCountdown}
                        startTime={moment(props.loginData.loginCodeCreationDate)}
                        endTime={moment(props.loginData.loginCodeCreationDate).add(
                            props.loginData.codeLifeTime,
                            'seconds'
                        )}
                    />
                </div>
            )}
            {hasLoginBtn && (
                <>
                    <div className={classes.loginButton}>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!props.isActive || isNotAllowedToSendSmsCode}
                            type="submit"
                            classes={{ root: classes.btnRoot }}
                        >
                            {getButtonText()}
                        </Button>
                        {isStageSMSCode && (
                            <Button
                                variant="text"
                                component="button"
                                disabled={!props.isActive}
                                classes={{ root: classes.btnRoot }}
                                onClick={goBackToInvitationCode}
                            >
                                {formatMessage(textMap.cancelBtnLabel)}
                            </Button>
                        )}
                    </div>
                    {!isSmallDevice && <GapBig />}
                </>
            )}
            {props.isLoading && <LoadingSpinner />}
            {hasQuickLoginBtn && <QuickLoginBtn />}
            {hasCancelBtn && (
                <div className={classes.cancelButton}>
                    <Button variant="text" component="button" classes={{ root: classes.btnRoot }} onClick={onCancel}>
                        {formatMessage(
                            props.stage === 'invitation-code' ? textMap.cancelBtnLabel : textMap.didntReceiveCode
                        )}
                    </Button>
                </div>
            )}
            {isModalVisible && <StartPageModal closeModal={closeModal} backToInvCode={goBackToInvitationCode} />}
            <Gap />
        </form>
    );
};

function getElementValue(form: any, name: string): string {
    return `${(form[name] && form[name].value) || ''}`.trim();
}

function mayBeValidInvitationCode(value: string): boolean {
    const regex = /^\+?([a-zA-Z0-9]| )*$/;
    return value.length > 4 && regex.test(value);
}

function mayBeValidSmsCode(code: string): boolean {
    const regex = /^[0-9]*$/;
    return code.length > 2 && regex.test(code);
}
